import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../axiosConfig';
import { useAuth } from '../contexts/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, activitiesRes] = await Promise.all([
        axios.get('/dashboard/stats'),
        axios.get('/dashboard/activities?limit=10')
      ]);

      setStats(statsRes.data.data);
      setActivities(activitiesRes.data.data);
      setError('');
    } catch (err) {
      console.error('대시보드 데이터 로드 실패:', err);
      setError('대시보드 데이터를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `${diffMins}분 전`;
    } else if (diffHours < 24) {
      return `${diffHours}시간 전`;
    } else if (diffDays < 7) {
      return `${diffDays}일 전`;
    } else {
      return date.toLocaleDateString('ko-KR');
    }
  };

  const shortcuts = [
    {
      path: '/seismic',
      title: '내진 설계 지원',
      description: '지도 기반 지반 분석 및 액상화 평가 결과를 확인합니다',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      color: 'from-green-500 to-green-600',
      hoverColor: 'hover:from-green-600 hover:to-green-700',
    },
    {
      path: '/upload',
      title: '신규 데이터 업로드',
      description: '새로운 시추 주상도 PDF를 업로드하고 좌표를 설정합니다',
      icon: (
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      color: 'from-blue-500 to-blue-600',
      hoverColor: 'hover:from-blue-600 hover:to-blue-700',
    },
  ];

  return (
    <div className="flex-1 overflow-auto bg-secondary-50">
      <div className="max-w-7xl mx-auto p-8 space-y-8">
        {/* 헤더 */}
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">
            KICT 시추주상도 관리 시스템
          </h1>
          <p className="text-secondary-600">
            지반공학 데이터 관리 및 내진 설계 지원 플랫폼
          </p>
        </div>

        {/* 바로가기 버튼 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {shortcuts.map((shortcut) => (
            <Link
              key={shortcut.path}
              to={shortcut.path}
              className={`block bg-gradient-to-br ${shortcut.color} ${shortcut.hoverColor} rounded-2xl p-8 text-white shadow-medium hover:shadow-hard transition-all duration-200 transform hover:-translate-y-1`}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 opacity-80">
                  {shortcut.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className="text-xl font-bold mb-2">{shortcut.title}</h3>
                  <p className="text-sm opacity-90">{shortcut.description}</p>
                </div>
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* 업로드 현황 */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4">업로드 현황</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">시추 주상도</p>
                  <p className="text-3xl font-bold text-secondary-900">
                    {loading ? '-' : (stats?.drill_log_count || 0)}
                    <span className="text-lg font-normal text-secondary-600 ml-2">개</span>
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">시추공</p>
                  <p className="text-3xl font-bold text-secondary-900">
                    {loading ? '-' : (stats?.borehole_count || 0)}
                    <span className="text-lg font-normal text-secondary-600 ml-2">개</span>
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 최근 활동 */}
        <div>
          <h2 className="text-xl font-bold text-secondary-900 mb-4">최근 활동</h2>
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                <p className="text-secondary-500 text-sm">로딩 중...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <p className="text-red-600">{error}</p>
              </div>
            ) : activities.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-12 h-12 text-secondary-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-secondary-500">최근 활동 내역이 없습니다</p>
              </div>
            ) : (
              <div className="divide-y divide-secondary-100">
                {activities.map((activity, index) => (
                  <div key={index} className="p-6 hover:bg-secondary-50 transition-colors">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-secondary-900">
                          {user?.role === 'admin' && (
                            <>
                              <span className="font-semibold">{activity.creator_name}</span> 님이&nbsp;
                            </>
                          )}
                          <span className="font-semibold">{activity.drill_log_name}</span> 파일을 업로드하여
                          <span className="font-semibold"> {activity.borehole_count}개</span>의 시추공 데이터가 추가되었습니다.
                        </p>
                        <p className="text-xs text-secondary-500 mt-1">
                          {formatTimestamp(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
